import { cn } from "@/lib/utils";
import { useUser } from "@/providers/user";
import { NotificationType } from "@/types/user/notification";
import { BadgeCheck, CircleAlert, CircleCheck, ExternalLink, Info, LucideProps, MailCheck, MailOpen, Megaphone, MessageCircle, OctagonX, PhoneCall, TicketCheck } from "lucide-react";
import { useRevalidator } from "react-router-dom";
import { Button } from "../ui/button";
import { SimpleTooltip } from "../ui/simple-tooltip";
import { NavLinkContainer } from "../utils/navlink-container";

export function NotificationIcon({ notification, className, ...props }: { notification: NotificationType } & LucideProps) {
    if (notification.type == "missed_call") return <PhoneCall className={cn(className)} {...props} />;
    if (notification.type == "sms") return <MessageCircle className={cn(className)} {...props} />;
    if (notification.type == "info") return <Info className={cn(className)} {...props} />;
    if (notification.type == "warning") return <CircleAlert className={cn(className)} {...props} />;
    if (notification.type == "success") return <CircleCheck className={cn(className)} {...props} />;
    if (notification.type == "subscribed") return <BadgeCheck className={cn("text-green-600", className)} {...props} />;
    if (notification.type == "unsubscribed") return <OctagonX className={cn("text-red-600", className)} {...props} />;
    if (notification.type == "subscribed_bonus") return <TicketCheck className={cn("text-green-600", className)} {...props} />;
    return <Megaphone className={cn(className)} {...props} />;
}

export function NotificationItem({
    notification,
    compact = false,
    link = false,
    onRedirect,
    revalidator,
    className,
    ...props
}: {
    notification: NotificationType;
    compact?: boolean;
    link?: boolean;
    onRedirect?: () => void;
    revalidator?: ReturnType<typeof useRevalidator>;
    props?: LucideProps;
} & React.HTMLAttributes<HTMLDivElement>) {
    const { mark_notification_as_seen, mark_notification_as_unseen } = useUser();
    const notificationUrl = notification.url || (notification.has_html_content ? `/notifications/${notification.id}` : null);

    const content = (
        <div>
            {notification.title && (
                <p className="flex items-center space-x-1 text-xs font-bold">
                    <span className="text-sm">{notification.title}</span>
                    {link && notificationUrl && <ExternalLink className="size-3" />}
                </p>
            )}
            {notification.content && <p className={cn("text-[11px]", compact ? "max-w-64 truncate" : "whitespace-pre-line")}>{notification.content}</p>}
        </div>
    );

    return (
        <div className={cn("flex items-center justify-between", className)} {...props}>
            <div className={cn("flex space-x-3", notification.seen ? "opacity-60" : "", compact ? "items-center" : "")}>
                <div>
                    <NotificationIcon notification={notification} className="size-5" />
                </div>
                {link && notificationUrl ? (
                    <NavLinkContainer
                        to={notification.url ? `${notification.url}?from_notification=${notification.id}` : notificationUrl}
                        onClick={onRedirect}
                    >
                        {content}
                    </NavLinkContainer>
                ) : (
                    content
                )}
            </div>
            {!notification.seen ? (
                <SimpleTooltip message="Marquer comme lu">
                    <Button size="icon" variant="ghost" onClick={() => mark_notification_as_seen(notification.id, () => revalidator?.revalidate())}>
                        <MailCheck className="size-4" />
                    </Button>
                </SimpleTooltip>
            ) : (
                !compact && (
                    <SimpleTooltip message="Marquer comme non lu">
                        <Button size="icon" variant="ghost" onClick={() => mark_notification_as_unseen(notification.id, () => revalidator?.revalidate())}>
                            <MailOpen className="size-4" />
                        </Button>
                    </SimpleTooltip>
                )
            )}
        </div>
    );
}
